import {
  Patientportal_AppointmentRequestProxyMsg,
  Patientportal_PortalAppointmentRequestFailureReason,
  Resource_AppointmentRequestStatus,
  useSubmitAppointmentRequestMutation,
} from '@/generated/graphql';
import { CloseCircleOutlined } from '@ant-design/icons';
import { AveliosButton } from '@components/common-layout/avelios-button';
import { Card, notification, StepProps, Steps } from 'antd';
import Title from 'antd/lib/typography/Title';
import { Maybe } from 'graphql/jsutils/Maybe';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import {
  AppointmentRequestStatusDescriptionTitles,
  AppointmentRequestStatusTitles,
  AppointmentRequestStatusTitlesShort,
} from '../../helpers/enum-maps';
import { buildTimestampDateTime } from '../../helpers/formatters';
import { useHandleAppointmentRequestFailureReason } from '../../helpers/use-handle-appointment-request-failure-reason';

const { Step } = Steps;

interface AppointmentRequestStatusProps {
  appointmentRequestProxy: Maybe<Patientportal_AppointmentRequestProxyMsg> | undefined;
}

interface AveliosStepProps extends StepProps {
  key: string | undefined | null;
  eventStatus: Maybe<Resource_AppointmentRequestStatus> | undefined;
  addition?: React.ReactNode;
}

const stepStatus = (
  status: Maybe<Resource_AppointmentRequestStatus> | undefined,
  currentStatus: Maybe<Resource_AppointmentRequestStatus> | undefined,
): 'wait' | 'process' | 'finish' | 'error' => {
  if (status === currentStatus) {
    switch (status) {
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusCancelled:
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusRejected:
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusProposalRejected:
        return 'error';
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusApproved:
        return 'finish';
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusArchived:
        return 'wait';
      default:
        return 'process';
    }
  }
  return 'process';
};

export const AppointmentRequestStatus: FC<AppointmentRequestStatusProps> = ({
  appointmentRequestProxy,
}) => {
  const { formatMessage } = useIntl();

  const appointmentRequest = appointmentRequestProxy?.appointmentRequest;
  const eventHistory = appointmentRequest?.eventHistory || [];

  const currentStatus = appointmentRequest?.status;

  const stepAddition = (
    status: Maybe<Resource_AppointmentRequestStatus> | undefined,
  ): React.ReactNode | undefined => {
    const appointmentRequest = appointmentRequestProxy?.appointmentRequest;
    switch (status) {
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusCreated:
        if (status === currentStatus) {
          return (
            <AveliosButton
              loading={submitLoading}
              type={'primary'}
              size={'small'}
              disabled={!appointmentRequestProxy?.clinicId || !appointmentRequest?.id}
              onClick={submitAppointmentRequest}
            >
              {formatMessage({ id: 'appointment-request.send' })}
            </AveliosButton>
          );
        }
        return null;
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusCancelled:
        const reasonsForCancellation = appointmentRequest?.reasonsForCancellation;
        if (reasonsForCancellation && reasonsForCancellation?.length > 0) {
          return (
            <span className={'ant-list-item-meta-description description'}>
              {formatMessage(
                { id: 'appointment-request.reason-for-cancellation' },
                {
                  reason: appointmentRequest?.reasonsForCancellation
                    ?.map((reason) => reason?.text)
                    ?.join(', '),
                },
              )}
            </span>
          );
        }
        return (
          <span style={{ color: 'darkgray' }}>
            {formatMessage({
              id: 'appointment-request.reason-for-cancellation.missing',
            })}
          </span>
        );
      case Resource_AppointmentRequestStatus.AppointmentRequestStatusRejected:
        const reasonsForRejectionExternal = appointmentRequest?.reasonsForRejectionExternal;
        if (reasonsForRejectionExternal && reasonsForRejectionExternal?.length > 0) {
          return (
            <>
              {formatMessage(
                { id: 'appointment-request.reason-for-rejection' },
                {
                  reason: reasonsForRejectionExternal?.map((reason) => reason?.text)?.join(', '),
                },
              )}
            </>
          );
        }
        return null;

      case Resource_AppointmentRequestStatus.AppointmentRequestStatusArchived:
        const reasonsForArchivingExternal = appointmentRequest?.reasonForArchivingExternal;
        if (reasonsForArchivingExternal) {
          return (
            <>
              {formatMessage(
                { id: 'appointment-request.reason-for-archived' },
                {
                  reason: reasonsForArchivingExternal,
                },
              )}
            </>
          );
        }
        return null;
      default:
        return null;
    }
  };

  const [handleAppointmentRequestsFailureReason] = useHandleAppointmentRequestFailureReason();

  const [submitAppointmentRequestMutation, { loading: submitLoading }] =
    useSubmitAppointmentRequestMutation();

  function showSubmitError() {
    notification.error({
      message: formatMessage({
        id: 'appointment-request.error',
      }),
      description: formatMessage({
        id: 'appointment-request.error.description',
      }),
    });
  }

  function submitAppointmentRequest() {
    if (appointmentRequestProxy?.clinicId && appointmentRequest?.id) {
      submitAppointmentRequestMutation({
        variables: {
          clinicId: appointmentRequestProxy.clinicId,
          appointmentRequestId: appointmentRequest?.id,
        },
      })
        // Only mutation rejections are handled here, so a failure inside the
        // success handler below cannot trigger a second error notification.
        .catch(() => {
          showSubmitError();
          return undefined;
        })
        .then((result) => {
          if (!result) {
            return;
          }
          const { data } = result;
          if (data?.submitAppointmentRequest?.appointmentRequest) {
            const failureReason = data.submitAppointmentRequest?.failureReason;
            if (
              !failureReason ||
              failureReason ===
                Patientportal_PortalAppointmentRequestFailureReason.PortalAppointmentRequestFailureReasonNone
            ) {
              notification.success({
                message: formatMessage({
                  id: 'appointment-request.success',
                }),
              });
            } else {
              // Handle failure reason
              handleAppointmentRequestsFailureReason(failureReason, {
                // re-submit request after handling completion
                next: () => submitAppointmentRequest(),
                onError: () => showSubmitError(),
              });
            }
          } else {
            showSubmitError();
          }
        });
    }
  }

  const statusSteps: AveliosStepProps[] | undefined = eventHistory?.map((event) => {
    const eventStatus = event?.requestStatus;

    return {
      key: event?.id,
      title: `${formatMessage({
        id: AppointmentRequestStatusTitlesShort.get(
          eventStatus || Resource_AppointmentRequestStatus.AppointmentRequestStatusNone,
        ),
      })} | ${buildTimestampDateTime(event?.timestamps?.createdAt)}`,
      description: formatMessage({
        id: AppointmentRequestStatusTitles.get(
          eventStatus || Resource_AppointmentRequestStatus.AppointmentRequestStatusNone,
        ),
      }),
      status: stepStatus(eventStatus, currentStatus),
      eventStatus,
      addition: stepAddition(eventStatus),
      icon:
        eventStatus === Resource_AppointmentRequestStatus.AppointmentRequestStatusArchived ? (
          <CloseCircleOutlined />
        ) : undefined,
    };
  });

  const getStatusMessage = () => {
    if (
      currentStatus !== Resource_AppointmentRequestStatus.AppointmentRequestStatusRejected &&
      currentStatus !== Resource_AppointmentRequestStatus.AppointmentRequestStatusArchived
    ) {
      return formatMessage({
        id: AppointmentRequestStatusDescriptionTitles.get(
          currentStatus || Resource_AppointmentRequestStatus.AppointmentRequestStatusNone,
        ),
      });
    }
    if (currentStatus === Resource_AppointmentRequestStatus.AppointmentRequestStatusRejected) {
      return formatMessage({ id: 'appointment-request.rejected.description' });
    }
    if (currentStatus === Resource_AppointmentRequestStatus.AppointmentRequestStatusArchived) {
      return formatMessage({ id: 'appointment-request.archived.description' });
    }
    return null;
  };

  return (
    <Card>
      <Title level={3} style={{ fontWeight: 'bold' }}>
        {formatMessage({ id: 'appointment-request.status' })}
      </Title>
      <p style={{ color: 'darkgray' }}>{getStatusMessage()}</p>
      <Steps
        direction="vertical"
        size="small"
        current={statusSteps ? statusSteps.length - 1 : undefined}
      >
        {statusSteps?.map(({ eventStatus: _, key, description, addition, ...props }) => (
          <Step
            key={key}
            {...props}
            description={
              <div>
                <div>{description}</div>
                {addition && <div style={{ marginTop: 10 }}>{addition}</div>}
              </div>
            }
          />
        ))}
      </Steps>
    </Card>
  );
};
